import { BaseApi } from '@/shared/api/baseApi';
import { API_ENDPOINTS } from '@/constants/api';
import { api } from '@/shared/api';
import { normalizePaginatedResponse } from '@/shared/api/pagination';
import type { PaginatedApiResponse } from '@/shared/api/pagination';
import type {
  Product,
  ProductFormData,
  ShopifyPrefillResponse,
  ProductSettings,
  ProductApiResponse,
  AvailableSwapProduct,
  AvailableSwapProductsResponse,
  AvailableGroupProductsResponse,
  ProductFilterOptions,
  ProductSearchFilters,
  ProductImage,
  ProductImageApiResponse,
  ProductImageSurface,
  ProductGrouping,
  ProductBadge,
  ProductBadgeFormData,
  BadgeOptions,
} from '../types';
import type {
  VarietyPackCandidate,
  VarietyPackItem,
  VarietyPackSaveResponse,
} from '../types/varietyPack';
import type { EntityEndpoints, PaginatedResponse } from '@/shared/types/entity';
import {
  transformProductFromApi,
  transformProductToApi,
  transformImageFromApi,
} from '../utils/transformers';
import { PRODUCT_IMAGE_API_SURFACES } from '../utils/productImages';

const PACK_CONFIGURATIONS = [
  'Multipack (same item)',
  'Multipack (variety)',
  'Single item',
  'Sleeve (same item)',
  'Sleeve (variety)',
  'Bundle',
];

const getProductFilterOptions = async (): Promise<ProductFilterOptions> => {
  const response = await api.get<ProductFilterOptions>(API_ENDPOINTS.PRODUCTS.FILTER_OPTIONS, {
    skipLoader: true,
  });
  return response.data;
};

const productsEndpoints: EntityEndpoints = {
  LIST: API_ENDPOINTS.PRODUCTS.LIST,
  CREATE: API_ENDPOINTS.PRODUCTS.CREATE,
  NEW: API_ENDPOINTS.PRODUCTS.NEW,
  SHOW: API_ENDPOINTS.PRODUCTS.SHOW,
  UPDATE: API_ENDPOINTS.PRODUCTS.UPDATE,
  DELETE: API_ENDPOINTS.PRODUCTS.SHOW, // Not used, but required by type
};

const productsApiInstance = new BaseApi<Product, ProductFormData>(productsEndpoints);

/**
 * Parameters for product list API request
 */
type ProductListParams = {
  page?: number;
  perPage?: number;
  search?: string;
  status?: ProductSearchFilters['status'];
  category?: string;
  allowedSubscriptions?: ProductSearchFilters['allowedSubscriptions'];
  hasSwapConfigured?: ProductSearchFilters['hasSwapConfigured'];
  isOneOff?: ProductSearchFilters['isOneOff'];
  isLowOnStock?: ProductSearchFilters['isLowOnStock'];
  includeDeleted?: ProductSearchFilters['includeDeleted'];
};

const toNumberOrNull = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const productsApi = {
  /**
   * Get paginated list of products with filters
   * Uses local loader instead of global overlay
   */
  getList: async (params?: ProductListParams): Promise<PaginatedResponse<Product>> => {
    const filteredParams = params
      ? Object.fromEntries(
          Object.entries(params).filter(([, value]) => value !== '' && value !== undefined),
        )
      : undefined;

    const response = await api.get<PaginatedApiResponse<ProductApiResponse> | ProductApiResponse[]>(
      API_ENDPOINTS.PRODUCTS.LIST,
      {
        params: filteredParams,
        skipLoader: true,
      },
    );

    // Handle both paginated response and plain array
    let apiData: ProductApiResponse[];
    let paginationData: Partial<PaginatedResponse<Product>>;

    if (Array.isArray(response.data)) {
      // Plain array response - transform to paginated format
      apiData = response.data;
      paginationData = {
        data: [],
        currentPage: params?.page || 1,
        perPage: params?.perPage || 20,
        total: apiData.length,
        lastPage: 1,
      };
    } else {
      // Paginated response
      const paginatedResponse = normalizePaginatedResponse(response.data);
      apiData = paginatedResponse.data;
      paginationData = {
        currentPage: paginatedResponse.currentPage,
        perPage: paginatedResponse.perPage,
        total: paginatedResponse.total,
        lastPage: paginatedResponse.lastPage,
      };
    }

    // Transform API products to frontend format
    const transformedData = apiData.map(transformProductFromApi);

    return {
      ...paginationData,
      data: transformedData,
    } as PaginatedResponse<Product>;
  },
  getOne: async (id: number): Promise<Product> => {
    const response = await api.get<ProductApiResponse>(API_ENDPOINTS.PRODUCTS.SHOW(id.toString()));
    return transformProductFromApi(response.data);
  },
  create: async (data: ProductFormData): Promise<Product> => {
    const apiData = transformProductToApi(data);
    const response = await api.post<ProductApiResponse>(API_ENDPOINTS.PRODUCTS.CREATE, apiData);
    return transformProductFromApi(response.data);
  },
  update: async (id: number, data: Partial<ProductFormData>): Promise<Product> => {
    const apiData = transformProductToApi(data);
    const response = await api.put<ProductApiResponse>(
      API_ENDPOINTS.PRODUCTS.UPDATE(id.toString()),
      apiData,
    );
    return transformProductFromApi(response.data);
  },
  updateBasicDetails: async (id: number, data: ProductFormData): Promise<Product> => {
    const response = await api.put<ProductApiResponse>(
      API_ENDPOINTS.PRODUCTS.BASIC_DETAILS(id.toString()),
      {
        shopifyId: toNumberOrNull(data.shopifyProductId),
        productVariantId: toNumberOrNull(data.shopifyVariantId),
        sku: data.sku,
        isVirtual: data.isVirtual,
        title: data.title,
        price: data.actualPrice,
        category: data.category,
        subCategory: data.subcategory,
        multipackType: data.packConfiguration,
        quantityDescription: data.quantityDescription,
      },
    );
    return transformProductFromApi(response.data);
  },
  updateSubscriptionConfiguration: async (id: number, data: ProductFormData): Promise<Product> => {
    const response = await api.put<ProductApiResponse>(
      API_ENDPOINTS.PRODUCTS.SUBSCRIPTION_CONFIGURATION(id.toString()),
      {
        isActive: data.isActive,
        allowedSubscriptions: data.allowedSubscriptions,
        isLowOnStock: data.lowStock,
        isOneOff: data.oneOff,
        swapWithVariantId: data.swapWithVariantId,
      },
    );
    return transformProductFromApi(response.data);
  },
  updatePromotionsBadges: async (id: number, data: ProductFormData): Promise<Product> => {
    const response = await api.put<ProductApiResponse>(
      API_ENDPOINTS.PRODUCTS.PROMOTIONS_BADGES(id.toString()),
      {
        listPrice: data.listPrice,
        badge: null,
      },
    );
    return transformProductFromApi(response.data);
  },
  updateAdditionalInformation: async (id: number, data: ProductFormData): Promise<Product> => {
    const response = await api.put<ProductApiResponse>(
      API_ENDPOINTS.PRODUCTS.ADDITIONAL_INFORMATION(id.toString()),
      {
        tags: data.tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0),
        aboutThisRecipe: data.aboutThisRecipe,
        description: data.description,
        shortDescription: data.shortDescription,
        upsellCarouselDescription: data.upsellCarouselDescription,
        composition: data.composition,
        analyticalConstituents: data.analyticalConstituents,
        nutritionalAdditives: data.nutritionalAdditives,
      },
    );
    return transformProductFromApi(response.data);
  },
  delete: (id: number): Promise<void> => {
    return productsApiInstance.delete(id);
  },
  /**
   * Prefill product data from Shopify by variant ID (for new products)
   * Returns shopifyId, productVariantId, title, sku, imageURL, price
   */
  prefill: async (
    variantId: string,
  ): Promise<{
    shopifyProductId: string;
    shopifyVariantId: string;
    title: string;
    primaryImageUrl: string | null;
    actualPrice: number | null;
    sku: string | null;
    images: ProductImage[];
  }> => {
    const response = await api.post<ShopifyPrefillResponse>(API_ENDPOINTS.PRODUCTS.PREFILL, {
      productVariantId: parseInt(variantId, 10),
    });
    const prefillImages = (response.data.images || []).map((img, idx) =>
      transformImageFromApi({
        ...img,
        imageUrl: img.url || img.imageUrl,
        sortOrder: typeof img.sortOrder === 'number' ? img.sortOrder : idx,
      }),
    );

    return {
      shopifyProductId: response.data.shopifyId?.toString() || '',
      shopifyVariantId: response.data.productVariantId?.toString() || '',
      title: response.data.title || '',
      primaryImageUrl: response.data.imageURL || null,
      actualPrice: response.data.price ?? null,
      sku: response.data.sku || null,
      images: prefillImages,
    };
  },

  refresh: async (variantId: string): Promise<Product> => {
    const response = await api.post<ProductApiResponse>(API_ENDPOINTS.PRODUCTS.REFRESH, {
      productVariantId: parseInt(variantId, 10),
    });
    return transformProductFromApi(response.data);
  },

  /**
   * Get product settings
   */
  getSettings: async (): Promise<ProductSettings> => {
    const options = await getProductFilterOptions();

    return {
      categories: options.categories,
      subcategories: options.subcategories,
      packConfigurations: PACK_CONFIGURATIONS,
    };
  },

  getImages: async (productId: number): Promise<ProductImage[]> => {
    const response = await api.get<ProductImageApiResponse[]>(
      API_ENDPOINTS.PRODUCTS.IMAGES(productId.toString()),
    );
    return (response.data || []).map(transformImageFromApi);
  },

  updateImage: async (
    productId: number,
    imageId: number,
    data: Pick<ProductImage, 'roles' | 'roleSortOrders' | 'sortOrder'>,
  ): Promise<ProductImage> => {
    const response = await api.put<ProductImageApiResponse>(
      API_ENDPOINTS.PRODUCTS.IMAGE(productId.toString(), imageId),
      {
        roles: data.roles,
        roleSortOrders: data.roleSortOrders,
        sortOrder: data.sortOrder,
      },
    );
    return transformImageFromApi(response.data);
  },

  syncImages: async (
    productId: number,
    images: ProductImage[],
    surface: ProductImageSurface,
  ): Promise<ProductImage[]> => {
    const response = await api.put<ProductImageApiResponse[]>(
      API_ENDPOINTS.PRODUCTS.IMAGES_SYNC(productId.toString()),
      {
        surface: PRODUCT_IMAGE_API_SURFACES[surface],
        images: images.map((image, index) => ({
          imageUrl: image.url,
          sortOrder: index,
          roles: image.roles,
          roleSortOrders: image.roleSortOrders,
        })),
      },
    );
    return (response.data || []).map(transformImageFromApi);
  },

  reorderImages: async (
    productId: number,
    images: { id: number; sortOrder: number }[],
  ): Promise<void> => {
    await api.put(API_ENDPOINTS.PRODUCTS.IMAGES(productId.toString()), { images });
  },

  bulkDeleteImages: async (productId: number, imageIds: number[]): Promise<void> => {
    await api.post(API_ENDPOINTS.PRODUCTS.IMAGES_BULK_DELETE(productId.toString()), { imageIds });
  },

  /**
   * Get available swap products for a given subscription category
   * @param subscription - Subscription category ('Cat' or 'Other')
   * @param excludeProductVariantId - Optional variant ID to exclude from results
   * @returns Array of available swap products
   */
  getAvailableSwapProducts: async (
    subscription: 'Cat' | 'Other',
    excludeProductVariantId?: number,
  ): Promise<AvailableSwapProduct[]> => {
    const params: Record<string, string | number> = {
      subscription,
    };

    if (excludeProductVariantId !== undefined) {
      params.excludeProductVariantId = excludeProductVariantId;
    }

    const response = await api.get<AvailableSwapProductsResponse | AvailableSwapProduct[]>(
      API_ENDPOINTS.PRODUCTS.AVAILABLE_SWAP_PRODUCTS,
      { params },
    );

    // Handle both array response and object with availableSwapProducts property
    if (Array.isArray(response.data)) {
      return response.data;
    }

    return response.data.availableSwapProducts || [];
  },

  /**
   * Get filter options for product search (categories, allowedSubscriptions)
   * Uses local loader instead of global overlay
   */
  getFilterOptions: getProductFilterOptions,

  /**
   * Get available products for grouping along with the current grouped product ID
   * @param productId - Current product ID
   * @returns Available products and the currently linked groupedProductId
   */
  getAvailableGroupProducts: async (productId: number): Promise<AvailableGroupProductsResponse> => {
    const response = await api.get<AvailableGroupProductsResponse>(
      API_ENDPOINTS.PRODUCT_GROUPINGS.AVAILABLE(productId.toString()),
    );
    return response.data;
  },

  /**
   * Create or update product grouping
   * @param productAId - First product ID
   * @param productBId - Second product ID
   * @returns Created/updated grouping
   */
  createOrUpdateGrouping: async (
    productAId: number,
    productBId: number,
  ): Promise<ProductGrouping> => {
    const response = await api.post<ProductGrouping>(API_ENDPOINTS.PRODUCT_GROUPINGS.CREATE, {
      productAId,
      productBId,
    });
    return response.data;
  },

  /**
   * Delete product grouping
   * @param productId - Product ID to remove grouping for
   */
  deleteGrouping: async (productId: number): Promise<void> => {
    await api.delete(API_ENDPOINTS.PRODUCT_GROUPINGS.DELETE(productId.toString()));
  },

  /**
   * Get all badges for a product
   */
  getBadges: async (productId: number): Promise<ProductBadge[]> => {
    const response = await api.get<ProductBadge[]>(
      API_ENDPOINTS.PRODUCTS.BADGES(productId.toString()),
    );
    return response.data;
  },

  /**
   * Sync all badges for a product (full replace)
   */
  syncBadges: async (
    productId: number,
    badges: ProductBadgeFormData[],
  ): Promise<ProductBadge[]> => {
    const apiData = {
      badges: badges.map((b) => ({
        content: b.content,
        displayLocation: b.displayLocation,
        style: b.style,
      })),
    };
    const response = await api.put<ProductBadge[]>(
      API_ENDPOINTS.PRODUCTS.BADGES(productId.toString()),
      apiData,
    );
    return response.data;
  },

  /**
   * Get available badge options (locations, styles)
   */
  getBadgeOptions: async (): Promise<BadgeOptions> => {
    const response = await api.get<BadgeOptions>(API_ENDPOINTS.BADGES.OPTIONS);
    return response.data;
  },

  /**
   * Get variety pack candidate products for a given product
   * Returns only Wet Food / Single item / same subcategory / active products
   */
  getVarietyPackCandidates: async (
    id: number,
    subCategory?: string | null,
  ): Promise<VarietyPackCandidate[]> => {
    const response = await api.get<VarietyPackCandidate[]>(
      API_ENDPOINTS.PRODUCTS.VARIETY_PACK_CANDIDATES(id.toString()),
      { params: subCategory ? { sub_category: subCategory } : undefined },
    );
    return response.data;
  },

  /**
   * Save variety pack configuration for a given product
   * Items with quantity 0 are accepted — backend will not persist them
   */
  saveVarietyPack: async (
    id: number,
    items: VarietyPackItem[],
  ): Promise<VarietyPackSaveResponse> => {
    const response = await api.put<VarietyPackSaveResponse>(
      API_ENDPOINTS.PRODUCTS.VARIETY_PACK(id.toString()),
      { items },
    );
    return response.data;
  },
};
